import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Geometry } from 'geojson';
import { api } from 'lib/request/api';
import type { ColorByOption } from 'components/color-by-attribute-modal';
import type {
  FilteredUwi,
  WellsFacetsResponse,
} from 'types/pre-drill-workspace';
import { AREA_COLOR_FACET_FIELDS, facetOptions } from './color-by';

export interface AreaColorFacets {
  /** The picklist for one attribute; empty until the query lands. */
  getOptions: (categoryId: string) => ColorByOption[];
  /** True while the query is in flight, so the modal can say so. */
  loading: boolean;
  // Why the values couldn't be loaded, or null. Without it a failed call is
  // indistinguishable from an area whose wells genuinely have no values.
  error: string | null;
  // The matched wells, each carrying every colourable attribute — the paint map is
  // grouped from these client-side, so switching attribute costs no round trip.
  wells: FilteredUwi[];
  /** Re-runs the call, so a modal opens on current values. */
  refetch: () => void;
}

/** Query-key prefix for the colour-by value lists. */
export const AREA_COLOR_FACETS_QUERY_KEY =
  'area-geometries:color-facets' as const;

// One identity for the empty case: the map's scoped layer reads this list, and a fresh
// array on every render would rebuild its paint expression for nothing.
const NO_WELLS: FilteredUwi[] = [];

// The value lists behind the colour-by modal, from one facets call scoped to the area's
// boundary — the path pre-drill uses, with the polygon standing in for its viewport box.
export const useAreaColorFacets = (
  geometry: Geometry | null | undefined
): AreaColorFacets => {
  // Serialised so a changed boundary re-queries, not a re-read of the same one.
  const key = geometry ? JSON.stringify(geometry) : null;

  const { data, isFetching, error, refetch } = useQuery<
    WellsFacetsResponse,
    Error
  >({
    queryKey: [AREA_COLOR_FACETS_QUERY_KEY, key],
    queryFn: ({ signal }) =>
      api
        .post('queries/wells', {
          json: {
            query: {
              polygon_geojson: geometry,
              facets: AREA_COLOR_FACET_FIELDS,
              includeFilteredUwis: true,
            },
          },
          signal,
        })
        .json<WellsFacetsResponse>(),
    enabled: key !== null,
    // Nothing here is retried silently: the pickers and the map both report an
    // empty list, so a hidden second attempt would only delay saying so.
    retry: false,
  });

  const facets = data?.facets ?? null;

  const getOptions = useCallback(
    (categoryId: string) => facetOptions(facets, categoryId),
    [facets]
  );

  // Nothing to ask for without a boundary, and a modal can open before one lands.
  const refetchScoped = useCallback(() => {
    if (geometry) {
      void refetch();
    }
  }, [geometry, refetch]);

  return {
    getOptions,
    loading: isFetching,
    error: error ? 'Could not load the values for this area.' : null,
    wells: data?.filteredUwis ?? NO_WELLS,
    refetch: refetchScoped,
  };
};
